/**
 * Internal Ticketed-Events Admission Backfill API Contract (U2 — R5, KTD5)
 *
 * POST /internal/ticketed-events/backfill-admissions
 *
 * The agent-runnable twin of `apps/backend/scripts/backfill-admissions-from-
 * order-items.ts`: gives every confirmed order item its `quantity`
 * purchase-origin admissions, one per seat. Both call the same backfill service,
 * so the run an agent performs and the run an operator performs cannot disagree.
 *
 * Only the wire shape lives here. The endpoint's own error enum stays beside its
 * handler in the backend, like every other `/internal/*` contract in this folder.
 */

import { z } from 'zod'

export const BackfillAdmissionsRequestSchema = z
  .object({
    /**
     * DEFAULTS TO A DRY RUN, and the default is the safety rail rather than a
     * convenience: an agent that forgets the field gets a report, never a write.
     */
    apply: z.boolean().default(false),
    /** Narrow to one event — the per-event unit, and the rehearsal shape. */
    eventPublicId: z.string().min(1).optional(),
    /**
     * Narrow to one school. Does not narrow further when `eventPublicId` is
     * given, but must still name a real school: every id the caller names is
     * checked, and an unknown one is a 404.
     */
    schoolPublicId: z.string().min(1).optional()
  })
  // AN APPLY MUST NAME ITS SCOPE. The operator script refuses a write without
  // `--confirm-prod`; this endpoint has no equivalent, so a bare `{apply: true}`
  // would write to every school with one request. A fleet-wide DRY RUN stays
  // allowed — it is how the multi-seat share is measured.
  .refine(body => !body.apply || body.eventPublicId !== undefined || body.schoolPublicId !== undefined, {
    message: 'apply requires eventPublicId or schoolPublicId; a fleet-wide apply is not allowed over this endpoint',
    path: ['apply']
  })
export type BackfillAdmissionsRequest = z.infer<typeof BackfillAdmissionsRequestSchema>

export const BackfillAdmissionsEventSummarySchema = z.object({
  eventPublicId: z.string(),
  itemsScanned: z.number().int(),
  itemsMinted: z.number().int(),
  itemsSkipped: z.number().int(),
  seatsMinted: z.number().int(),
  partiallyMintedItems: z.number().int(),
  multiSeatItems: z.number().int(),
  confirmedItems: z.number().int(),
  /** Seats given their item's check-in stamp; on a dry run, the number it would give (SCH-1758). */
  checkInsCarried: z.number().int()
})
export type BackfillAdmissionsEventSummary = z.infer<typeof BackfillAdmissionsEventSummarySchema>

/**
 * The run report. The backend use-case returns exactly this type, so a field
 * added here without being populated there (or the reverse) fails to compile.
 */
export const BackfillAdmissionsResponseSchema = z.object({
  apply: z.boolean(),
  eventsScanned: z.number().int(),
  itemsMinted: z.number().int(),
  itemsSkipped: z.number().int(),
  seatsMinted: z.number().int(),
  partiallyMintedItems: z.number().int(),
  multiSeatItems: z.number().int(),
  confirmedItems: z.number().int(),
  /** Multi-seat items as a percentage of confirmed items; the plan bounds it at 2%. */
  multiSeatSharePercent: z.number(),
  /**
   * Confirmed order items still short of their `quantity` in purchase-origin
   * admissions. ZERO IS THE ONLY PASSING VALUE — this is the check KTD5 puts
   * between the backfill and the read flip.
   */
  violations: z.number().int(),
  /** Seats given their item's check-in stamp; on a dry run, the number it would give (SCH-1758). */
  checkInsCarried: z.number().int(),
  /**
   * Confirmed order items whose check-in stamp disagrees with their live seats',
   * counted after the run and scoped like `violations`. ZERO IS THE ONLY PASSING
   * VALUE before the P3 read flip. Separate from `violations`, which stays the
   * capacity repair's refusal test (SCH-1758).
   */
  checkInParityViolations: z.number().int(),
  events: z.array(BackfillAdmissionsEventSummarySchema)
})
export type BackfillAdmissionsResponse = z.infer<typeof BackfillAdmissionsResponseSchema>
